import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { isRecoverableFailure } from "../features/connectivity/connectivity-state.ts";
import { connectivityStore } from "../features/connectivity/connectivity-store.ts";
import { backoffDelayMs } from "../features/connectivity/retry.ts";

/** Bounded retry window (FR-OFF-013: no silent infinite retry). */
const MAX_QUERY_RETRIES = 3;

function reportOutcome(error: unknown): void {
  connectivityStore.dispatch(
    error
      ? { type: "request-failure", recoverable: isRecoverableFailure(error) }
      : { type: "request-success" },
  );
}

/**
 * Retry only recoverable (network-type) failures, never a 4xx (FR-OFF-005/009).
 * `failureCount` is the number of attempts already made when React Query asks
 * whether to retry again, so this caps total attempts at
 * `MAX_QUERY_RETRIES + 1`.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  return isRecoverableFailure(error) && failureCount < MAX_QUERY_RETRIES;
}

export const queryClient = new QueryClient({
  // Every query/mutation outcome (success or failure) feeds the shared
  // connectivity model (FR-OFF-001) — this is the "or a lightweight probe"
  // half of the connectivity signal, complementing `navigator.onLine`.
  queryCache: new QueryCache({
    onSuccess: () => reportOutcome(undefined),
    onError: (error) => reportOutcome(error),
  }),
  mutationCache: new MutationCache({
    onSuccess: () => reportOutcome(undefined),
    onError: (error) => reportOutcome(error),
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: shouldRetry,
      // FR-OFF-005: exponential backoff (base 1s, factor 2, cap 30s) + full jitter.
      retryDelay: (attemptIndex) => backoffDelayMs(attemptIndex),
      refetchOnWindowFocus: false,
      // FR-OFF-007: resync authoritative state on reconnect.
      refetchOnReconnect: true,
    },
    mutations: {
      retry: shouldRetry,
      retryDelay: (attemptIndex) => backoffDelayMs(attemptIndex),
    },
  },
});
